// Backoffice → Inicio → «Carruseles del Inicio»: cada pestaña se guarda por separado y lo que
// guardan los demás aparece solo (components/HomeShowcaseEditor.jsx). Aquí, sin React, la cuenta
// de qué pestañas tienen cambios sin guardar y cómo se junta lo que llega del servidor con lo que
// hay en pantalla. Lo vigila tests/carruseles-guardado-por-pestana.test.mjs.
//
// Con cada pestaña se recuerda de qué versión del servidor parte (`bases[pestaña]`: su huella,
// que se devuelve al guardar, y su contenido). Una pestaña tiene cambios sin guardar si lo que hay
// en pantalla no es aquello de lo que partía.

import '../../../renderer/tabs/inicio/componentes/destacados-modelo.js';

const M = globalThis.TCHomeShowcase;

export const GALLERY_TAB = 'gallery';
// Lo que se guarda por separado, en el orden de las pestañas (como en el Inicio de la app).
export const SAVE_SECTIONS = ['games', 'widgets', 'animations', GALLERY_TAB];

/** Un apartado, limpio y como texto: para saber si ha cambiado. */
export const sectionJson = (config, section) => JSON.stringify(M.sanitize(config)[section] || []);

/** De dónde parte cada apartado: la huella del servidor (se devuelve al guardar) y su contenido. */
export const baseOf = (data, section) => ({ stamp: (data.stamps && data.stamps[section]) || '', json: sectionJson(data.config, section) });

/** La configuración limpia, siempre con los destacados (vacíos si no hay). */
export const cleanConfig = (raw) => {
  const config = M.sanitize(raw);
  if (!config.gallery) config.gallery = [];
  return config;
};

/** Lo que llega del servidor al abrir el editor. */
export function freshDoc(data) {
  return { form: cleanConfig(data.config), bases: Object.fromEntries(SAVE_SECTIONS.map((s) => [s, baseOf(data, s)])), conflicts: {}, server: data };
}

/** ¿Esa pestaña tiene cambios sin guardar? */
export const isDirty = (doc, section) => sectionJson(doc.form, section) !== doc.bases[section].json;

/** ¿Es una lectura más vieja que lo que ya hay (salió antes de un guardado y llega después)? */
export const isStale = (doc, data) => !!(doc && doc.server && doc.server.updatedAt && data.updatedAt && data.updatedAt < doc.server.updatedAt);

/**
 * Las pestañas sin cambios cuya versión del servidor ha cambiado: las ha guardado otro.
 * (Para el aviso «… se ha actualizado con lo que guardó …».)
 */
export function changedByOthers(doc, data) {
  if (!doc || isStale(doc, data)) return [];
  return SAVE_SECTIONS.filter((s) => {
    const mine = sectionJson(doc.form, s);
    return mine === doc.bases[s].json && baseOf(data, s).json !== mine;
  });
}

/**
 * Lo nuevo del servidor encima de lo que hay en pantalla. Una pestaña sin cambios toma lo
 * guardado (lo de otros admins incluido); una con cambios sin guardar se queda como está y, si
 * alguien la ha guardado mientras tanto, se apunta el aviso (`conflicts`). `saved` = la pestaña
 * que se acaba de guardar y lo que se mandó ({ section, json }): toma lo guardado salvo que se
 * haya seguido editando mientras se guardaba.
 */
export function mergeFresh(doc, data, saved = null) {
  if (isStale(doc, data)) return doc;
  const config = cleanConfig(data.config);
  const form = { ...doc.form };
  const bases = { ...doc.bases };
  const conflicts = { ...doc.conflicts };
  SAVE_SECTIONS.forEach((s) => {
    const mine = sectionJson(doc.form, s);
    const next = baseOf(data, s);
    const clean = mine === doc.bases[s].json || mine === next.json;
    if (saved && saved.section === s) {
      if (mine === saved.json) form[s] = config[s];
      bases[s] = next;
      delete conflicts[s];
    } else if (clean) {
      form[s] = config[s];
      bases[s] = next;
      delete conflicts[s];
    } else if (next.stamp !== doc.bases[s].stamp) {
      conflicts[s] = (data.sections && data.sections[s]) || {};
    }
  });
  return { form, bases, conflicts, server: data };
}

/** «Ver lo suyo»: la pestaña vuelve a lo último leído del servidor (se descartan los cambios). */
export function discardSection(doc, section) {
  const conflicts = { ...doc.conflicts };
  delete conflicts[section];
  return {
    ...doc,
    form: { ...doc.form, [section]: cleanConfig(doc.server.config)[section] },
    bases: { ...doc.bases, [section]: baseOf(doc.server, section) },
    conflicts
  };
}
